import { useState } from 'react'
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Sports, Person } from '@mui/icons-material'
import { useRecruiterProfileForm } from '../hooks/useRecruiterProfileForm'
import { ContactInfoFields } from '../components/profile/recruiter/ContactInfoFields'
import { RecruitingFocusFields } from '../components/profile/recruiter/RecruitingFocusFields'

const steps = ['Personal Information', 'Recruiting Focus']

/**
 * The 2-step onboarding wizard. Used only by `/onboarding` — once a profile is
 * complete, editing happens on the single-page `/profile/edit` form instead.
 * There is no college step: the recruiter's school is chosen at signup and
 * verified against their email, so it isn't theirs to edit.
 */
export const RecruiterProfileWizard = () => {
  const form = useRecruiterProfileForm()
  const [activeStep, setActiveStep] = useState(0)

  const handleNext = () => {
    if (form.validateStep(activeStep as 0 | 1)) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    form.setFieldErrors({})
    setActiveStep(prev => prev - 1)
  }

  const handleSave = () => {
    void form.save()
  }

  if (form.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ContactInfoFields form={form} />
      case 1:
        return <RecruitingFocusFields form={form} />
      default:
        return null
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {activeStep === 0 && <Person sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {activeStep === 1 && <Sports sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {steps[activeStep]}
      </Typography>

      {form.saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Profile saved successfully!
        </Alert>
      )}
      {form.isSaveHttpError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save profile. Please try again.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {renderStepContent(activeStep)}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button variant="contained" onClick={handleSave} disabled={form.isSaving}>
                  {form.isSaving ? 'Saving...' : 'Save Profile'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
